import { useState, useMemo } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import PageTransition from '@/components/ui/PageTransition';
import { useListResourcesQuery, type Resource, type ResourceType, type ListClientResourcesParams } from '@/api/mcp-appointments';
import { useEffectiveCompanyId } from '@/contexts/AdminClientsContext';
import { CreateResourceModal } from '@/components/resources/CreateResourceModal';
import { EditResourceModal } from '@/components/resources/EditResourceModal';
import { DeleteResourceModal } from '@/components/resources/DeleteResourceModal';
import {
  ResourcesHeader,
  ResourcesStats,
  ResourcesFilters,
  ResourcesTable,
  type ResourcesFilterState
} from '@/components/resources/list';
import { createManageRouteGuard } from '@/utils/manageRouteGuards';
import { TENANT_ROLES } from '@/constants/tenantRoles';
import { useClientListFilters } from '@/hooks/useClientListFilters';

export const Route = createFileRoute('/manage/company-resources/')({
  beforeLoad: createManageRouteGuard({ requiredTenantRoles: [TENANT_ROLES.OWNER] }),
  component: ResourcesPage
});

/** Default filters of the resources list; also what "clear all" restores. */
const INITIAL_FILTERS: ResourcesFilterState = { searchText: '', typeFilter: 'all', languageFilter: '' };

function ResourcesPage() {
  const effectiveCompanyId = useEffectiveCompanyId();

  const [selectedResource, setSelectedResource] = useState<Resource | null>(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  const { filters, updateFilter, resetFilters, hasActiveFilters, currentPage, pageSize, paginate, handlePageChange } =
    useClientListFilters<ResourcesFilterState>(INITIAL_FILTERS);
  const queryParams: ListClientResourcesParams = useMemo(
    () => ({
      companyId: effectiveCompanyId || undefined,
      type: filters.typeFilter === 'all' ? undefined : filters.typeFilter,
      name: filters.searchText || undefined,
      languageCode: filters.languageFilter || undefined
    }),
    [effectiveCompanyId, filters.typeFilter, filters.searchText, filters.languageFilter]
  );

  const { data: resources, isLoading } = useListResourcesQuery(queryParams, true);

  const stats = useMemo(() => {
    if (!resources)
      return {
        total: 0,
        byType: { PERSON: 0, ROOM: 0, EQUIPMENT: 0, VEHICLE: 0 },
        isLoading
      };

    const byType = resources.reduce(
      (acc, resource) => {
        acc[resource.type] = (acc[resource.type] || 0) + 1;
        return acc;
      },
      {} as Record<ResourceType, number>
    );

    return {
      total: resources.length,
      byType: {
        PERSON: byType.PERSON || 0,
        ROOM: byType.ROOM || 0,
        EQUIPMENT: byType.EQUIPMENT || 0,
        VEHICLE: byType.VEHICLE || 0
      },
      isLoading
    };
  }, [resources, isLoading]);

  const paginatedResources = paginate(resources);

  return (
    <PageTransition animation="fadeInUp">
      <ResourcesHeader onAddClick={() => setCreateModalOpen(true)} />

      <ResourcesStats stats={stats} />

      <ResourcesFilters
        filters={filters}
        filteredCount={resources?.length ?? 0}
        totalCount={resources?.length ?? 0}
        hasActiveFilters={hasActiveFilters}
        onFilterChange={updateFilter}
        onReset={resetFilters}
      />

      <ResourcesTable
        resources={paginatedResources}
        total={resources?.length ?? 0}
        hasActiveFilters={hasActiveFilters}
        isLoading={isLoading}
        currentPage={currentPage}
        pageSize={pageSize}
        onPageChange={handlePageChange}
        onResetFilters={resetFilters}
        onEditResource={(r) => {
          setSelectedResource(r);
          setEditModalOpen(true);
        }}
        onDeleteResource={(r) => {
          setSelectedResource(r);
          setDeleteModalOpen(true);
        }}
      />

      <CreateResourceModal open={createModalOpen} onClose={() => setCreateModalOpen(false)} />

      <EditResourceModal
        open={editModalOpen}
        resource={selectedResource}
        onClose={() => {
          setEditModalOpen(false);
          setSelectedResource(null);
        }}
      />

      <DeleteResourceModal
        open={deleteModalOpen}
        resource={selectedResource}
        onClose={() => {
          setDeleteModalOpen(false);
          setSelectedResource(null);
        }}
      />
    </PageTransition>
  );
}
